import { mendTeal } from '@mend/mui/colors';
import { Box } from '@mui/material';

import { hex2rgba } from '#utils/colors.ts';

/** The tint marking an already-selected option. */
const selectedBgColor = hex2rgba(mendTeal[700], 0.08);

interface OptionRowProps {
  props: Omit<React.HTMLAttributes<HTMLLIElement>, 'key' | 'children'>;
  selected: boolean;
  children: React.ReactNode;
  /** Set by a virtualized listbox, which owns where in the scroll region the row sits. */
  style?: React.CSSProperties;
}

/**
 * The shell every autocomplete option row in this folder shares: the selected tint, the divider
 * between rows, and a full-width slot for whatever the row says. Shared so that two pickers listing
 * the same person can't drift apart visually.
 *
 * **Its rendered height is load-bearing.** The provider picker virtualizes its listbox, which
 * positions rows by multiplying a fixed number (`providerOptionHeight`) rather than measuring them —
 * so anything here that changes a row's height has to be reflected there, or that popup's rows
 * overlap.
 */
export function OptionRow({ props, selected, children, style }: OptionRowProps) {
  return (
    <Box
      {...props}
      component="li"
      style={style}
      sx={{
        backgroundColor: selected ? selectedBgColor : undefined,
        '&:not(:last-child)': {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box width="100%">{children}</Box>
    </Box>
  );
}
